// Shared confirm/deny dialog (app.md §8). Confirms an Approve; requires a
// comment for any Deny / Archive. For bulk, the same single comment applies to
// every selected invoice (the body text says so explicitly).
import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  MessageBar,
  MessageBarBody,
  Option,
  Textarea,
} from '@fluentui/react-components'
import { REASON_CATEGORIES, reasonCategoryLabel } from '../lib/constants'

interface Props {
  open: boolean
  title: string
  /** Confirmation prose, e.g. "Approve 12 invoices?". */
  body?: string
  /** When true, a non-empty reason is required before confirm enables. */
  requireReason: boolean
  reasonLabel?: string
  /** Placeholder for the reason box (e.g. an example reason). */
  reasonPlaceholder?: string
  /** Show the optional deny/archive reason-category dropdown alongside the
      reason box (deny + archive outcomes). The category is never required —
      the free-text reason stays the record of why. */
  showReasonCategory?: boolean
  confirmLabel: string
  destructive?: boolean
  /** Extra class for the confirm button (e.g. the green approve fill). */
  confirmClassName?: string
  /** Optional caution note (e.g. the "approval commits — no undo" warning). */
  note?: string
  submitting?: boolean
  onConfirm: (reason: string, category?: string) => void
  onCancel: () => void
}

export function DecisionDialog({
  open,
  title,
  body,
  requireReason,
  reasonLabel = 'Reason',
  reasonPlaceholder = 'Required — visible in the decision history.',
  showReasonCategory,
  confirmLabel,
  destructive,
  confirmClassName,
  note,
  submitting,
  onConfirm,
  onCancel,
}: Props) {
  const [reason, setReason] = useState('')
  // The picked category KEY (e.g. 'price_discrepancy'), or '' for none. Optional.
  const [reasonCategory, setReasonCategory] = useState('')

  useEffect(() => {
    if (open) {
      setReason('')
      setReasonCategory('')
    }
  }, [open])

  const canConfirm = !submitting && (!requireReason || reason.trim().length > 0)

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open && !submitting) onCancel() }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            {body && <p style={{ marginTop: 0 }}>{body}</p>}
            {requireReason && (
              <Field label={reasonLabel} required>
                <Textarea
                  value={reason}
                  onChange={(_, d) => setReason(d.value)}
                  placeholder={reasonPlaceholder}
                  resize="vertical"
                  rows={3}
                />
              </Field>
            )}
            {showReasonCategory && (
              <Field label="Category (optional)" style={{ marginTop: 12 }}>
                <Dropdown
                  placeholder="Pick a category"
                  selectedOptions={reasonCategory ? [reasonCategory] : []}
                  value={reasonCategoryLabel(reasonCategory)}
                  onOptionSelect={(_, d) => setReasonCategory(d.optionValue ?? '')}
                >
                  {Object.entries(REASON_CATEGORIES).map(([key, label]) => (
                    <Option key={key} value={key} text={label}>
                      {label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            )}
            {note && (
              <MessageBar intent="warning" style={{ marginTop: 12 }}>
                <MessageBarBody>{note}</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              className={confirmClassName}
              style={destructive ? { backgroundColor: '#D13438' } : undefined}
              disabled={!canConfirm}
              onClick={() => onConfirm(reason.trim(), reasonCategory || undefined)}
            >
              {submitting ? 'Submitting…' : confirmLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
